import { useCallback, useEffect, useRef, useState } from 'react';

const refreshMS = 15000;
const maxBackoffMS = 120000;

// One operation per identity. Refresh is scheduled after completion, never
// invalidates an in-flight success, and cannot defeat failure backoff.
export default function useKeyOptionsQueryV1110(key, enabled, load) {
  const [result, setResult] = useState({ key: '', ready: false });
  const retryRef = useRef(() => {});
  const retry = useCallback(() => retryRef.current(), []);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let pending = false;
    let failures = 0;
    let nextAt = 0;
    let timer;
    let controller;
    const visible = () => document.visibilityState !== 'hidden';
    const schedule = () => {
      clearTimeout(timer);
      if (active && visible())
        timer = setTimeout(() => run(), Math.max(0, nextAt - Date.now()));
    };
    const run = async (manual = false) => {
      if (!active || pending || !visible()) return;
      if (!manual && Date.now() < nextAt) return schedule();
      clearTimeout(timer);
      pending = true;
      controller = new AbortController();
      setResult((previous) => ({
        ...(previous.key === key ? previous : { key, ready: false }),
        refreshing: true,
      }));
      try {
        const data = await load(controller.signal);
        if (!active) return;
        failures = 0;
        setResult({ key, data, ready: true, error: null, refreshing: false });
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        failures += 1;
        setResult((previous) => ({
          ...previous,
          key,
          ready: false,
          error,
          refreshing: false,
        }));
      } finally {
        pending = false;
        if (active) {
          const delay = Math.min(
            maxBackoffMS,
            refreshMS * 2 ** Math.min(failures, 3)
          );
          nextAt = Date.now() + delay;
          schedule();
        }
      }
    };
    const refresh = () => {
      if (!visible()) {
        clearTimeout(timer);
        return;
      }
      // Focus may refresh a successful result, but never stacks work or
      // bypasses failure backoff. Policy changes use a new effect identity.
      run(failures === 0);
    };
    retryRef.current = () => run(true);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    run();
    return () => {
      active = false;
      retryRef.current = () => {};
      clearTimeout(timer);
      controller?.abort();
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [key, enabled, load]);
  const current = enabled && result.key === key;
  return {
    data: current ? result.data : undefined,
    ready: Boolean(current && result.ready),
    error: current ? result.error : null,
    loading: Boolean(enabled && (!current || result.refreshing)),
    retry,
  };
}
